import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import {
  OFFERINGS_MATCH_PATH,
  OFFERINGS_SEARCH_PATH,
} from '@de/utils/configurable-step/offeringsResultsSourceIdentity';

/**
 * Body fields an Offerings search must have resolved before it may run.
 *
 * The general HTTP rule is that body templates are allowed through
 * unresolved, because most POST sources use optional criteria and JSON
 * serialization drops `undefined` fields harmlessly. Offerings
 * `POST /v1/offerings/search` and `/search/match` are the exception:
 * campaign-management availability requires a geo key when budgeting, and
 * firing with an unresolved `{{formState.geolocation}}` produces a 400 that
 * the manager then aborts and refires once the value lands.
 *
 * This lives in its own module rather than inside the dependency checker so
 * the checker stays free of any per-type knowledge. Both paths share the
 * contract, and the gate is path-only — it does not inspect `hasBudget`, and
 * deferral only trips when `geolocation` is templated and still unresolved.
 */
export function offeringsRequiredBodyFields(
  decl: DataSourceDeclaration,
): readonly unknown[] {
  if (!isOfferingsSearchOrMatchPost(decl)) {
    return [];
  }
  const body = (decl as { body?: unknown }).body;
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    return [];
  }
  return [(body as Record<string, unknown>).geolocation];
}

function isOfferingsSearchOrMatchPost(decl: DataSourceDeclaration): boolean {
  const candidate = decl as { method?: unknown; endpoint?: unknown };
  if (candidate.method !== 'POST' || typeof candidate.endpoint !== 'string') {
    return false;
  }
  const path = extractPath(candidate.endpoint);
  return path === OFFERINGS_SEARCH_PATH || path === OFFERINGS_MATCH_PATH;
}

function extractPath(endpoint: string): string {
  if (!endpoint) return '';
  if (endpoint.startsWith('/')) return endpoint.split('?')[0] ?? '';
  try {
    const parsed = new URL(endpoint);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return '';
    }
    return parsed.pathname;
  } catch {
    return '';
  }
}
